import { Card } from '../ui/Card';
import { ArrowUpRight, ArrowDownRight, type LucideIcon } from 'lucide-react';
import clsx from 'clsx';

type StatCardProps = {
  kicker: string;
  value: string;
  delta?: number;
  hint?: string;
  icon?: LucideIcon;
  accent?: 'brand' | 'success' | 'warn' | 'danger' | 'neutral';
};

const accentRing: Record<NonNullable<StatCardProps['accent']>, string> = {
  brand:   'from-etsy-300/50 via-etsy-200/10',
  success: 'from-sage/30 via-sage/5',
  warn:    'from-amber/30 via-amber/5',
  danger:  'from-rose/30 via-rose/5',
  neutral: 'from-ink-200/30 via-ink-200/5',
};

export function StatCard({ kicker, value, delta, hint, icon: Icon, accent = 'brand' }: StatCardProps) {
  const positive = (delta ?? 0) >= 0;
  return (
    <Card className="p-5 relative overflow-hidden">
      {/* Warm radial bloom in the corner */}
      <div className={clsx('absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br to-transparent blur-2xl opacity-70', accentRing[accent])} />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="kicker">{kicker}</div>
          {Icon && <Icon size={13} className="text-ink-400" strokeWidth={1.7} />}
        </div>
        {/* Serif number — the Etsy / boutique feel */}
        <div className="serif text-[36px] leading-[1.05] text-ink-800 tabular-nums font-semibold">{value}</div>
        <div className="flex items-center gap-2 mt-2">
          {delta !== undefined && (
            <div
              className={clsx(
                'inline-flex items-center gap-0.5 text-[11.5px] font-medium px-1.5 py-0.5 rounded-md',
                positive ? 'text-sage bg-sage/10' : 'text-rose bg-rose/10',
              )}
            >
              {positive ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
              {Math.abs(delta).toFixed(1)}%
            </div>
          )}
          {hint && <div className="text-[11.5px] text-ink-500">{hint}</div>}
        </div>
      </div>
    </Card>
  );
}
